/*
  The trail of what a shopper opened lives in their own browser and nowhere
  else: product ids, in this origin's local storage, no account, nothing sent
  anywhere and nothing that survives a cleared profile. That is enough for the
  row, and it is the least a shop needs to know to draw one.

  The cards themselves come from Shopify, because the browser holds ids and only
  the platform can turn ids into priced, buyable cards. The row asks the search
  endpoint for the same section file a second time and lifts the cards out of
  the answer.
*/

const SA_RECENT_STORE = 'sa-recent';

function saRecentRead() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SA_RECENT_STORE) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => Number.isInteger(id) && id > 0);
  } catch (error) {
    // Private browsing, storage switched off, a profile cleared mid-visit. The
    // row is a convenience, so it goes quiet rather than throwing on a page
    // somebody is trying to buy from.
    return [];
  }
}

function saRecentWrite(ids) {
  try {
    window.localStorage.setItem(SA_RECENT_STORE, JSON.stringify(ids));
  } catch (error) {
    // Same again: a trail is worth less than the page it sits on.
  }
}

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const limit = parseInt(this.dataset.limit, 10) || 4;
    this.ids = this.record().slice(0, limit);
    if (!this.ids.length) return;

    // The row sits well down the page, so it waits until it is nearly in view.
    // The element is full width and no height until it has something to show,
    // which is exactly what an observer needs to watch.
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  record() {
    const current = parseInt(this.dataset.currentProduct, 10);
    const keep = parseInt(this.dataset.remember, 10) || 8;
    const stored = saRecentRead();
    if (!Number.isInteger(current) || current <= 0) return stored;

    const trail = [current, ...stored.filter((id) => id !== current)].slice(0, keep);
    saRecentWrite(trail);

    // What somebody is reading right now is not something to offer them back.
    return trail.filter((id) => id !== current);
  }

  load() {
    const params = new URLSearchParams({
      q: this.ids.map((id) => `id:${id}`).join(' OR '),
      type: 'product',
      section_id: this.dataset.sectionId,
    });

    fetch(`${this.dataset.url}?${params}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        const answer = new DOMParser().parseFromString(text, 'text/html');
        const grid = this.querySelector('[data-recent-grid]');
        const body = this.querySelector('[data-recent-body]');
        if (!grid || !body) return;

        // Search answers by relevance. The row is about the order they were
        // opened in, so the ids put the cards back in that order.
        this.ids.forEach((id) => {
          const card = answer.querySelector(`[data-recent-card][data-product-id="${id}"]`);
          if (!card) return;
          this.scopeQuickAdd(card);
          grid.appendChild(card);
        });

        if (!grid.children.length) return;
        body.hidden = false;
        this.setAttribute('data-loaded', '');
      })
      .catch(() => {
        // A row that cannot load stays the nothing it already was.
      });
  }

  /*
    A card's quick add modal is named after the product alone, so the same pack
    showing in this row and in the shelf above it would put the same id on the
    page twice and every opener would reach the first one. The row is the guest
    here, so the row's copy takes the longer name.
  */
  scopeQuickAdd(card) {
    const modal = card.querySelector('quick-add-modal[id]');
    const opener = card.querySelector('modal-opener[data-modal]');
    if (!modal || !opener) return;

    const scoped = `${modal.id}-recent`;
    opener.dataset.modal = `#${scoped}`;
    modal.id = scoped;

    card.querySelectorAll('[id^="QuickAddInfo-"], [id^="ModalClose-"]').forEach((node) => {
      node.id = `${node.id}-recent`;
    });
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
